import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { getUserLocalePreferences } from "./sharedActions";

// NOTE(liamvdv): We use selectors and middlewares over hooks for a reason.
//                The code is a bit more distributed and difficult to follow,
//                but we need to programatically control the locale and theme
//                for the SDK and Platform Preview.
//                This also ensures consistent behavior across the i18n
//                localization and frame settings localization.

/////////////////////////// THEME SELECTORS ///////////////////////////

export const getSystemTheme = (): "light" | "dark" =>
  window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

// Simple selector for theme preference
const selectThemePreference = (state: RootState) => state.config.data.theme;

// NOTE(liamvdv): middle triggers re-render when system theme changes
// Memoized selector that computes effective theme
export const selectTheme = createSelector(
  [selectThemePreference],
  (preference): "light" | "dark" => {
    return preference === "auto" ? getSystemTheme() : preference;
  }
);

/////////////////////////// LOCALE SELECTORS ///////////////////////////

const selectFrameId = (state: RootState) => state.config.data.frameId;
const selectFrameState = (state: RootState) => state.frame;
const selectLocale = (state: RootState) => state.config.data.locale;

// Memoized selector for current frame settings
export const selectCurrentFrameSettings = createSelector(
  [selectFrameId, selectFrameState],
  (frameId, frameState) => ({
    data: frameState.data[frameId],
    status: frameState.status[frameId] || "idle",
    error: frameState.error[frameId] || null,
  })
);

export const selectEffectiveLocale = createSelector(
  [selectCurrentFrameSettings, selectLocale],
  ({ data, status }, userPreference) => {
    // Return undefined if frame settings aren't loaded
    if (status !== "succeeded" || !data?.locales?.length) {
      return undefined;
    }

    // If user has explicitly set a preference and it's available, use it
    if (userPreference) {
      // exact match
      const userLocale = data.locales.find(
        (l) => l.locale.toLowerCase() === userPreference.toLowerCase()
      );
      if (userLocale) return userLocale.locale;

      // prefix match (locale: de | available locales are de-DE, en-UK, ...) -> de-DE
      const partialMatch = data.locales.find((l) =>
        l.locale.toLowerCase().startsWith(userPreference.toLowerCase())
      );
      if (partialMatch) return partialMatch.locale;
    }

    // Otherwise, pick based on browser preferences
    const selectedLocale = pickLocale(
      getUserLocalePreferences(),
      data.locales,
      (l) => l.locale
    );

    return selectedLocale?.locale;
  }
);

function getLocaleMatchWeigth(a: string, b: string): number {
  a = a.toLowerCase();
  b = b.toLowerCase();
  const aParts = a.split("-");
  const bParts = b.split("-");

  if (a === b) return 5;
  else if (aParts[0] === bParts[0]) return 2;
  else return 0;
}

// Pick the locale that best matches the user's preferences:
// 1. prefer earlier position in the list over later position in the list.
// 2. prefer more specific en-US over en match. Useful for geetings differences in de-CH vs de-DE vs de-AT.
// 3. prefer showing some locale over no bubble.
export function pickLocale<T extends Record<string, any>>(
  preferences: string[],
  objects: T[],
  key: (o: T) => string
): T {
  if (objects.length === 0) {
    throw new Error("[botbrains]: No locales available; hiding bubble.");
  }
  const fallbackOrder = ["de", "en"];
  const descPreferences = [...preferences, ...fallbackOrder];
  for (const locale of descPreferences) {
    const ranking = objects.map(
      (o) => [o, getLocaleMatchWeigth(locale, key(o))] as const
    );
    const max = Math.max(...ranking.map((r) => r[1]));
    if (max > 0) {
      return ranking.find((r) => r[1] === max)![0];
    }
  }
  console.warn(
    "[botbrains]: Locale Matching returned no results, fallback to any locale."
  );
  return objects[0];
}

export const selectChallengeHeaders = (frameId: string) => (state) =>
  state.challenge.data[frameId]?.headers || {};
